export const firstDefined = (obj, keys = []) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

export const normalizeCostingCodeMap = (value) => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [key, raw]) => {
    const dimensionCode = Number(key);
    if (!Number.isFinite(dimensionCode)) return acc;
    acc[dimensionCode] = raw === undefined || raw === '' ? null : raw;
    return acc;
  }, {});
};

export const normalizeCostingDimensions = (rawDimensions, fallbackCodes = {}) => {
  const dimensions = (rawDimensions ?? [])
    .map((d) => {
      const dimensionCode = Number(firstDefined(d, ['dimensionCode', 'DimensionCode']));
      if (!Number.isFinite(dimensionCode)) return null;
      return {
        dimensionCode,
        name: firstDefined(d, ['name', 'Name']),
        description: firstDefined(d, ['description', 'Description']),
        displayName: firstDefined(d, ['displayName', 'DisplayName']) ?? `Costing Code ${dimensionCode}`,
        isActive: firstDefined(d, ['isActive', 'IsActive']) !== false
      };
    })
    .filter((d) => d?.isActive)
    .sort((a, b) => a.dimensionCode - b.dimensionCode);

  if (dimensions.length) return dimensions;

  const fallbackKeys = Object.keys(fallbackCodes)
    .map(Number)
    .filter(Number.isFinite);
  const codes = fallbackKeys.length ? fallbackKeys : [1, 2];

  return [...new Set(codes)].sort((a, b) => a - b).map((dimensionCode) => ({
    dimensionCode,
    name: null,
    description: null,
    displayName: dimensionCode === 1 ? 'Costing Code' : `Costing Code ${dimensionCode}`,
    isActive: true
  }));
};

export const costingCodesFromDecision = (decision, dimensions = []) => {
  const direct = normalizeCostingCodeMap(firstDefined(decision, ['costingCodes', 'CostingCodes']));
  const suggested = normalizeCostingCodeMap(firstDefined(decision, ['suggestedCostingCodes', 'SuggestedCostingCodes']));
  if (Object.keys(direct).length) {
    const merged = { ...direct };
    Object.entries(suggested).forEach(([dimensionCode, value]) => {
      if ((merged[dimensionCode] === undefined || merged[dimensionCode] === null || merged[dimensionCode] === '') && value) {
        merged[dimensionCode] = value;
      }
    });
    return normalizeCostingCodeMap(merged);
  }

  if (Object.keys(suggested).length) return suggested;

  const legacy = {};
  const code1 = firstDefined(decision, ['costingCode', 'CostingCode', 'costCenterCode', 'CostCenterCode']);
  const code2 = firstDefined(decision, ['costingCode2', 'CostingCode2', 'distributionRule', 'DistributionRule']);
  if (code1 !== null) legacy[1] = code1;
  if (code2 !== null) legacy[2] = code2;

  return normalizeCostingCodeMap(
    dimensions.length
      ? Object.fromEntries(dimensions.map((d) => [d.dimensionCode, legacy[d.dimensionCode] ?? null]))
      : legacy
  );
};

export const withLegacyCostingFields = (payload) => {
  const costingCodes = normalizeCostingCodeMap(payload?.costingCodes ?? payload?.CostingCodes);
  return {
    ...payload,
    costingCodes,
    costingCode: costingCodes[1] ?? payload?.costingCode ?? null,
    costingCode2: costingCodes[2] ?? payload?.costingCode2 ?? null
  };
};

export const setCostingCodeValue = (codes, dimensionCode, value) => ({
  ...normalizeCostingCodeMap(codes),
  [Number(dimensionCode)]: value || null
});

export const costingCodeOptionsByDimension = (items = []) =>
  (items ?? []).reduce((acc, item) => {
    const dimensionCode = Number(firstDefined(item, ['inWhichDimension', 'InWhichDimension']));
    const code = firstDefined(item, ['code', 'Code']);
    if (!Number.isFinite(dimensionCode) || !code) return acc;
    const name = firstDefined(item, ['name', 'Name']);
    const option = {
      value: code,
      label: name ? `${code} - ${name}` : code
    };
    acc[dimensionCode] = [...(acc[dimensionCode] ?? []), option];
    return acc;
  }, {});

export const costingCodeSummary = (codes, dimensions = []) => {
  const normalized = normalizeCostingCodeMap(codes);
  const entries = (dimensions.length ? dimensions : normalizeCostingDimensions(null, normalized))
    .map((d) => {
      const value = normalized[d.dimensionCode];
      return value ? `${d.displayName}: ${value}` : null;
    })
    .filter(Boolean);
  return entries.join(' | ');
};
